/* eslint-disable no-bitwise */
import {
  createTopLevelCustomNodesObjectArray,
  UnderlinedClassnames,
} from 'src/grammar-checker/utils';
import { IReplacementObject, Suggestion } from 'src/grammar-checker/models/api';
import { QuillEditorIDS } from '../../_common/handlers/quillEditor';
import {
  EmptyString,
  GcErrorContainer,
  InlineSuggestionId,
  Space,
} from '../constants/common.constants';

export interface IOverlayRangeData {
  startNode: Node;
  endNode: Node;
  startIndex: number;
  endOffset: number;
}

export interface IgnoreWordInterface {
  word: string;
  startIndex: number;
  endIndex: number;
}
export interface RecomputeOverlayArgs {
  nodesToProcess?: Node[];
  paraLevelGrammarResponse: {
    [originalTextHash: number]: IReplacementObject[];
  };
  ignoredWords: IgnoreWordInterface[];
  setGrammarErrorCount: Function;
  setGrammarCheckRunning: Function;
  nodeIndexToSkip?: Set<number>;
  openGrammarCheckCorrectionCard: Function;
  setErrorCount: Boolean;
  setState: Function;
  forceRepaintAllErrors: boolean;
}

export type ElemRect = {
  width: number;
  height: number;
  top: number;
  left: number;
  // For rendering the highlights, we create two containers
  // One is the size of visible part of the editor, and another (a child of the first)
  // is the size of the full editor (including scrolling)
  // Underlines are rendered inside the scrolling container
  // and when the editor is scrolled, the parent container is scrolled as well
  // keeping the underlines in the correct position
  // that is why we use the scrollHeight and scrollWidth
  scrollHeight?: number;
  scrollWidth?: number;
};

export const getUnifiedClientRects = (
  boundingRect: DOMRect,
  clientRects: DOMRectList | DOMRect[]
): ElemRect[] => {
  /* assuming support only for ltr language */
  const elemRects: ElemRect[] = [];
  try {
    let lineCount = 0;
    let top = 0;
    const elemRectObject: {
      [line: string]: { left: number; right: number; height: number; top: number };
    } = {};
    if (
      clientRects &&
      boundingRect &&
      clientRects[clientRects.length - 1] &&
      clientRects[clientRects.length - 1].top === boundingRect.top
    ) {
      /* single line, return boundingClientBox */
      elemRects.push({
        width: boundingRect.width,
        top: boundingRect.top,
        height: boundingRect.height,
        left: boundingRect.left,
      });
      return elemRects;
    }
    for (let i = 0; i < clientRects.length; i += 1) {
      if (clientRects[i].top !== top || top === 0) {
        top = clientRects[i].top;
        lineCount += 1;
      }
      if (!elemRectObject[lineCount]) {
        elemRectObject[lineCount] = {
          left: clientRects[i].left,
          right: clientRects[i].right,
          top: clientRects[i].top,
          height: clientRects[i].height,
        };
      } else {
        if (clientRects[i].left < elemRectObject[lineCount].left) {
          elemRectObject[lineCount].left = clientRects[i].left;
        }
        if (clientRects[i].right > elemRectObject[lineCount].right) {
          elemRectObject[lineCount].right = clientRects[i].right;
        }
        if (clientRects[i].height > elemRectObject[lineCount].height) {
          elemRectObject[lineCount].height = clientRects[i].height;
        }
      }
    }

    Object.keys(elemRectObject).forEach((key) => {
      elemRects.push({
        width: elemRectObject[key].right - elemRectObject[key].left,
        top: elemRectObject[key].top,
        left: elemRectObject[key].left,
        height: elemRectObject[key].height,
      });
    });
  } catch (e) {
    console.log('error in getUnifiedRects', e);
  }
  return elemRects;
};

// https://jkorpela.fi/chars/spaces.html
// https://r12a.github.io/app-conversion/
const DifferentSpaceUnicodeDecimalValue = [65279, 160]; // [U+FEFF, U+A0];

export const hashCode = (message: string) => {
  let hash = 0;
  if (message.length === 0) return hash;
  for (let i = 0; i < message.length; i += 1) {
    const char = message.charCodeAt(i);
    // skip all other spaces (non-breaking, zero-width etc)
    if (!DifferentSpaceUnicodeDecimalValue.includes(char)) {
      hash = (hash << 5) - hash + char;
      hash &= hash; // Convert to 32bit integer
    }
  }
  return hash;
};

// Resets all the errors painted through overlay
export const resetOverlayContainer = () => {
  const overlayContainer = document.getElementById(GcErrorContainer) as HTMLElement;
  if (overlayContainer) {
    overlayContainer.innerHTML = '';
  }
};

export const addSelectionForReplacementForHTML = (_range: Range) =>
  new Promise((resolve) => {
    setTimeout(() => {
      const selection = document.getSelection() as Selection;

      // Remove all existing selection
      if (selection.rangeCount > 0) {
        selection.removeAllRanges();
      }

      // Update the selection with current range
      selection.addRange(_range);

      // !below piece of code is causing issue in safari as the below equality check fails
      // while (selection.getRangeAt(0) !== _range) {
      // selection.removeAllRanges();
      // selection.addRange(_range);
      // }

      resolve(selection);
    }, 10);
  });

export const getAllChildrenOfResearchEditor = () => {
  const allChildrenNodes: any[] = [];

  document.getElementById(QuillEditorIDS.RESEARCH_EDITOR)?.childNodes.forEach((childNode) => {
    allChildrenNodes.push(childNode);
  });

  return allChildrenNodes;
};

export const getIdForGcError = (key: number, startIndex: number, endOffset: number) =>
  `p${key}s${startIndex}e${endOffset}`;

export const isInViewport = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();

  // This function checks if given element lies in viewport
  return (
    (rect.top >= 0 && rect.top <= window.innerHeight) ||
    (rect.bottom <= window.innerHeight && rect.bottom >= 0) ||
    (rect.top < 0 && rect.bottom > window.innerHeight)
  );
};

export const getRangeData = ({
  textNodes,
  contentToReplace,
}: {
  textNodes: any[];
  contentToReplace: any;
}) => {
  let startNode: Node | undefined;
  let endNode: Node | undefined;

  let startIndex = 0;
  let endOffset = 0;
  try {
    // eslint-disable-next-line space-in-parens
    for (let i = 0; i < textNodes.length; ) {
      if (
        // skipping if the current error is not present in the current text node
        contentToReplace.startIndex > textNodes[i].endIndex
      ) {
        i += 1;
      } else if (
        // case in which the current error lies in current node only, ie. not in any other node
        contentToReplace.startIndex >= textNodes[i].startIndex &&
        contentToReplace.endIndex <= textNodes[i].endIndex
      ) {
        startNode = textNodes[i].node;
        endNode = textNodes[i].node;
        startIndex = contentToReplace.startIndex - textNodes[i].startIndex;
        endOffset = contentToReplace.endIndex - textNodes[i].startIndex + 1;
        i += 1;
        break;
      } else if (
        // case in which the current error lies in multiple nodes
        contentToReplace.startIndex >= textNodes[i].startIndex &&
        contentToReplace.endIndex > textNodes[i].endIndex
      ) {
        startNode = textNodes[i].node;
        startIndex = contentToReplace.startIndex - textNodes[i].startIndex;

        // Keep looping through nodes until we get to endNode with endOffset
        while (true) {
          if (contentToReplace.endIndex > textNodes[i].endIndex) {
            i += 1;
          } else {
            endNode = textNodes[i].node;
            endOffset = contentToReplace.endIndex - textNodes[i].startIndex + 1;
            i += 1;
            break;
          }
        }
        break;
      }
    }
  } catch (e) {
    console.log('error in getRangeData', e, contentToReplace);
  }

  return {
    startNode,
    startIndex,
    endNode,
    endOffset,
  };
};
